/**
 * LMA Variant Metaobject Updater
 * Handles dynamic updates of product variant metaobject data
 */

// Global variables
let productMetaObjectData = {};
let variantMetaObjectsData = {};

// Initialize the script
function initializeVariantUpdater() {
  // Load metaobject data from JSON script
  try {
    const dataScript = document.getElementById("lma-metaobject-data");
    if (dataScript) {
      const metaObjectData = JSON.parse(dataScript.textContent);
      productMetaObjectData = metaObjectData.productData || {};
      variantMetaObjectsData = metaObjectData.variantData || {};
    } else {
      setFallbackData();
    }
  } catch (error) {
    setFallbackData();
  }

  // Define custom element if not already defined
  if (!customElements.get("product-sales-highlight")) {
    class ProductSalesHighlight extends HTMLElement {
      constructor() {
        super();
        this._content = "";
      }

      connectedCallback() {
        this._content = this.textContent;
      }

      updateContent(content) {
        try {
          if (content && content !== "null" && content.trim() !== "") {
            this.textContent = content;
            this.style.display = "inline";
          } else {
            this.style.display = "none";
          }
        } catch (error) {
          this.style.display = "none";
        }
      }

      get type() {
        return this.getAttribute("type");
      }
    }

    // Register the custom element
    customElements.define("product-sales-highlight", ProductSalesHighlight);
  }

  // Set up event listeners
  setupEventListeners();

  // Perform initial update
  performInitialUpdate();
}

// Set fallback data structure
function setFallbackData() {
  productMetaObjectData = {
    price_per_measurement: null,
    price_per_measurement_note: "",
    unit_label: { raw: "", parts: { singular: "", plural: "" } },
    unit_of_measurement: {
      raw: "",
      parts: { singular: "", plural: "", abbreviation: "" },
    },
    measured_area: "",
    name_of_product: "",
    space_calculator_enabled: false,
    discount_title: "",
    discount_expiration: "",
    discount_code: "",
    tariff_price_info: "",
    includes: "",
    special_banner: "",
  };
  variantMetaObjectsData = {};
}

// Function to safely get variant data with product-level fallbacks
function getVariantData(variantId) {
  const variantData = variantMetaObjectsData[variantId] || {};

  // Helper function to check if a value is empty/null
  const isEmpty = (value) => {
    if (value === null || value === undefined || value === "null") return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (typeof value === "object" && value.raw === "null") return true;
    if (typeof value === "object" && value.raw === "") return true;
    return false;
  };

  // Create merged data with fallbacks
  const mergedData = { ...variantData };

  // Apply fallbacks for simple fields
  const simpleFields = [
    "price_per_measurement",
    "price_per_measurement_note",
    "measured_area",
    "name_of_product",
    "discount_title",
    "discount_expiration",
    "discount_code",
    "tariff_price_info",
    "includes",
    "special_banner",
  ];

  simpleFields.forEach((field) => {
    if (isEmpty(mergedData[field])) {
      mergedData[field] = productMetaObjectData[field];
    }
  });

  // Apply fallbacks for boolean fields
  if (
    mergedData.space_calculator_enabled === null ||
    mergedData.space_calculator_enabled === undefined
  ) {
    mergedData.space_calculator_enabled =
      productMetaObjectData.space_calculator_enabled;
  }

  // Apply fallbacks for complex fields (unit_label and unit_of_measurement)
  if (isEmpty(mergedData.unit_label)) {
    mergedData.unit_label = productMetaObjectData.unit_label;
  } else if (isEmpty(mergedData.unit_label?.raw)) {
    // If variant has the structure but empty raw value, replace the whole object
    mergedData.unit_label = productMetaObjectData.unit_label;
  }

  if (isEmpty(mergedData.unit_of_measurement)) {
    mergedData.unit_of_measurement = productMetaObjectData.unit_of_measurement;
  } else if (isEmpty(mergedData.unit_of_measurement?.raw)) {
    // If variant has the structure but empty raw value, replace the whole object
    mergedData.unit_of_measurement = productMetaObjectData.unit_of_measurement;
  }

  return mergedData;
}

// Function to update single text field content
function updateSingleTextFields(variantData) {
  if (!variantData) return;

  const highlights = document.querySelectorAll("product-sales-highlight");

  highlights.forEach((element) => {
    const type = element.type;

    try {
      switch (type) {
        case "sales-note":
          element.updateContent(variantData.price_per_measurement_note);
          updateContainer(element, variantData.price_per_measurement_note);
          break;

        case "tariff":
          element.updateContent(variantData.tariff_price_info);
          updateContainer(element, variantData.tariff_price_info);
          break;

        case "discount-title":
          element.updateContent(variantData.discount_title);
          break;

        case "discount-expiry":
          element.updateContent(variantData.discount_expiration);
          break;

        case "discount-code":
          element.updateContent(variantData.discount_code);
          break;
      }
    } catch (error) {
      // Silent error handling
    }
  });
}

// Helper function to show/hide parent containers
function updateContainer(element, content, containerSelector = null) {
  try {
    const container = containerSelector
      ? document.querySelector(containerSelector)
      : element.closest(".product__sales-note, .product__price--tariff");

    if (container) {
      if (content && content !== "null" && content.trim() !== "") {
        container.style.display = "block";
      } else {
        container.style.display = "none";
      }
    }
  } catch (error) {
    // Silent error handling
  }
}

// Function to update price per measurement calculation
function updatePricePerMeasurement(variant, variantData) {
  if (!variant || !variantData) return;

  try {
    const priceElement = document.querySelector(
      ".product__price--per-measurement"
    );
    if (!priceElement) return;

    let pricePerUnit;
    let calculationMethod;

    // Check if variant has direct price_per_measurement value
    if (
      variantData.price_per_measurement &&
      variantData.price_per_measurement !== null
    ) {
      // Use the direct price_per_measurement value from metaobject
      pricePerUnit = parseFloat(variantData.price_per_measurement).toFixed(2);
      calculationMethod = "direct";
    } else {
      // Fall back to calculation
      const variantPrice = variant.price;
      const priceInDollars = variantPrice / 100;

      // Get measured area from metaobject data or fallback to data attribute
      const measuredArea =
        parseFloat(variantData.measured_area) ||
        parseFloat(priceElement.getAttribute("data-measured-area")) ||
        1;

      // Calculate price per measurement unit
      pricePerUnit = (priceInDollars / measuredArea).toFixed(2);
      calculationMethod = "calculated";
    }

    // Get unit abbreviation from metaobject data or fallback to existing unit label
    const unitAbbr = variantData.unit_of_measurement?.parts?.abbreviation;
    let unitLabel;

    if (
      unitAbbr &&
      unitAbbr !== "null" &&
      unitAbbr !== "NA" &&
      unitAbbr.trim() !== ""
    ) {
      unitLabel = ` per ${unitAbbr}`;
    } else {
      // Fallback to existing unit label
      const existingUnitLabel = priceElement.querySelector(".unit-label");
      unitLabel = existingUnitLabel ? existingUnitLabel.textContent : "";
    }

    // Update the price per measurement element
    priceElement.innerHTML = `$${pricePerUnit}<span class="unit-label">${unitLabel}</span>`;
  } catch (error) {
    // Silent error handling
  }
}

// Function to update original price per measurement calculation
function updateOriginalPricePerMeasurement(variant, variantData) {
  if (!variant || !variantData) return;

  try {
    const compareContainer = document.querySelector(
      ".product__price--compare-container"
    );
    const comparePerMeasurementElement = document.querySelector(
      ".product__price--compare-per-measurement"
    );

    if (!compareContainer) return;

    // Check if variant has compare_at_price
    if (
      !variant.compare_at_price ||
      variant.compare_at_price <= variant.price
    ) {
      // Hide the entire compare container if no compare_at_price or not greater than price
      compareContainer.style.display = "none";
      return;
    }

    // Show the container if it was hidden
    compareContainer.style.display = "block";

    // Only update the per-measurement part if the element exists and we have measured area
    if (comparePerMeasurementElement && variantData.measured_area) {
      const compareAtPrice = variant.compare_at_price;
      const priceInDollars = compareAtPrice / 100;

      // Get measured area from metaobject data
      const measuredArea = parseFloat(variantData.measured_area) || 1;

      // Calculate original price per measurement unit
      const originalPricePerUnit = (priceInDollars / measuredArea).toFixed(2);

      // Get unit abbreviation from metaobject data
      const unitAbbr = variantData.unit_of_measurement?.parts?.abbreviation;

      // Update the original price per measurement element
      if (
        unitAbbr &&
        unitAbbr !== "null" &&
        unitAbbr !== "NA" &&
        unitAbbr.trim() !== ""
      ) {
        comparePerMeasurementElement.innerHTML = `$${originalPricePerUnit} per ${unitAbbr} / `;
      } else {
        // Fallback to a default unit if no abbreviation is available
        comparePerMeasurementElement.innerHTML = `$${originalPricePerUnit} per unit / `;
      }
    }
  } catch (error) {
    // Silent error handling
  }
}

// Function to update price divider visibility
function updatePriceDivider(variant, variantData) {
  if (!variant || !variantData) return;

  // Add a small delay to ensure price-off badge has been updated by theme JS
  setTimeout(() => {
    try {
      const priceDivider = document.querySelector(".lma-price-divider");
      const priceOffBadge = document.querySelector("[data-price-off]");

      if (!priceDivider) return;

      // Check conditions for showing the divider:
      // 1. Must have measured_area
      // 2. Must be on sale (compare_at_price > price)
      // 3. Price-off badge should be visible (not hidden)

      const hasMeasuredArea =
        variantData.measured_area && parseFloat(variantData.measured_area) > 0;
      const isOnSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      const priceOffVisible =
        priceOffBadge && !priceOffBadge.classList.contains("hidden");

      if (hasMeasuredArea && isOnSale && priceOffVisible) {
        priceDivider.style.display = "block";
      } else {
        priceDivider.style.display = "none";
      }
    } catch (error) {
      // Silent error handling
    }
  }, 0.1); // 100ms delay to allow theme JS to update price-off badge
}

// Function to update savings display
function updateSavings(variant) {
  if (!variant) return;

  try {
    const savingsElement = document.querySelector(".product__price--savings");
    if (!savingsElement) return;

    // Check if variant has savings (compare_at_price > price)
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const savings = variant.compare_at_price - variant.price;

      // Check if currency code is enabled by looking for existing price formatting
      const existingPriceElement = document.querySelector(
        "[data-product-price]"
      );
      const currencyCodeEnabled =
        existingPriceElement &&
        (existingPriceElement.textContent.includes("USD") ||
          existingPriceElement.textContent.includes("CAD") ||
          existingPriceElement.textContent.includes("EUR") ||
          existingPriceElement.textContent.includes("GBP"));

      // Format savings with appropriate currency formatting
      let formattedSavings;
      if (currencyCodeEnabled) {
        // Use Shopify's money_with_currency format equivalent
        formattedSavings = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD", // Default currency, could be made dynamic
          currencyDisplay: "code",
        }).format(savings / 100);
      } else {
        // Use regular money format equivalent
        formattedSavings = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(savings / 100);
      }

      // Update the savings element
      savingsElement.textContent = `Savings: ${formattedSavings}`;
      savingsElement.style.display = "inline";
    } else {
      // Hide savings if no discount
      savingsElement.style.display = "none";
    }
  } catch (error) {
    // Silent error handling
  }
}

// Function to update unit fields content
function updateUnitFields(variantData) {
  if (!variantData) return;

  try {
    // Update unit labels and measurements using product-sales-highlight
    const updateHighlights = (type, value) => {
      const elements = document.querySelectorAll(
        `product-sales-highlight[type="${type}"]`
      );
      elements.forEach((element) => {
        // Treat "NA" as empty value
        const cleanValue = value === "NA" || value === "null" ? "" : value;
        element.updateContent(cleanValue);
      });
    };

    // Update unit labels
    if (variantData.unit_label?.parts) {
      updateHighlights(
        "unit-label-singular",
        variantData.unit_label.parts.singular
      );
      updateHighlights(
        "unit-label-plural",
        variantData.unit_label.parts.plural
      );
      updateHighlights("unit-label-raw", variantData.unit_label.raw);
    }

    // Update unit measurements
    if (variantData.unit_of_measurement?.parts) {
      updateHighlights(
        "unit-measurement-singular",
        variantData.unit_of_measurement.parts.singular
      );
      updateHighlights(
        "unit-measurement-plural",
        variantData.unit_of_measurement.parts.plural
      );
      updateHighlights(
        "unit-measurement-abbreviation",
        variantData.unit_of_measurement.parts.abbreviation
      );
      updateHighlights(
        "unit-measurement-raw",
        variantData.unit_of_measurement.raw
      );
    }
  } catch (error) {
    // Silent error handling
  }
}

// Function to update floor calculator and quantity selector data
function updateFloorCalculatorData(variantData) {
  if (!variantData) return;

  try {
    // Update calculator data attributes
    const calculatorData = document.querySelector("#calculator-data");
    if (calculatorData && variantData.measured_area) {
      calculatorData.setAttribute(
        "data-measured-area",
        variantData.measured_area
      );

      // Update unit labels if available
      if (variantData.unit_label?.parts?.singular) {
        calculatorData.setAttribute(
          "data-unit-label",
          variantData.unit_label.parts.singular
        );
      }
      if (variantData.unit_label?.parts?.plural) {
        calculatorData.setAttribute(
          "data-unit-label-plural",
          variantData.unit_label.parts.plural
        );
      }
    }

    // Update quantity updater data attributes
    const quantityUpdaterData = document.querySelector(
      "#quantity-updater-data"
    );
    if (quantityUpdaterData) {
      if (variantData.measured_area) {
        quantityUpdaterData.setAttribute(
          "data-measured-area",
          variantData.measured_area
        );
      }
      if (variantData.unit_of_measurement?.parts?.abbreviation) {
        quantityUpdaterData.setAttribute(
          "data-unit-of-measurement",
          variantData.unit_of_measurement.parts.abbreviation
        );
      }
    }

    // Recalculate floor calculator if space size is entered
    const spaceSizeInput = document.querySelector("#space-size");
    if (
      spaceSizeInput &&
      spaceSizeInput.value &&
      parseFloat(spaceSizeInput.value) > 0
    ) {
      // Trigger the existing calculateAndUpdate function if it exists
      if (typeof window.calculateAndUpdate === "function") {
        window.calculateAndUpdate();
      } else if (typeof calculateAndUpdate === "function") {
        calculateAndUpdate();
      }
    }

    // Update quantity selector with new variant data
    // TODO2: Comment for Now
    //updateQuantitySelectorWithNewData(variantData);
  } catch (error) {
    // Silent error handling
  }
}

// Function to update quantity selector with new variant data and refresh coverage
function updateQuantitySelectorWithNewData(variantData) {
  try {
    const quantitySelector = document.querySelector(".quantity__selector");
    if (!quantitySelector) return;

    const measuredArea = parseFloat(variantData.measured_area) || 0;
    const unitAbbr =
      variantData.unit_of_measurement?.parts?.abbreviation || "units";

    // Create updated coverage function that reads from current variant data
    function updateCoverageWithNewData(quantity) {
      if (measuredArea > 0) {
        const totalCoverage = quantity * measuredArea;
        const totalCoverageElement = document.querySelector("#total-coverage");
        const unitOfMeasurementElement = document.querySelector(
          "#unit-of-measurement"
        );

        if (totalCoverageElement) {
          totalCoverageElement.textContent = totalCoverage.toFixed(0);
        }
        if (unitOfMeasurementElement) {
          unitOfMeasurementElement.textContent = unitAbbr;
        }
      }
    }

    // Update current coverage display
    const currentQuantity = parseInt(quantitySelector.value) || 1;
    updateCoverageWithNewData(currentQuantity);

    // Override the global updateCoverage function to use new data
    if (typeof window !== "undefined") {
      window.updateCoverage = updateCoverageWithNewData;
    }

    // Re-bind event listeners with new data to ensure they work properly
    const decreaseBtn = document.querySelector(".quantity__btn--decrease");
    const increaseBtn = document.querySelector(".quantity__btn--increase");

    if (decreaseBtn) {
      // Remove existing listeners and add new ones
      const newDecreaseBtn = decreaseBtn.cloneNode(true);
      decreaseBtn.parentNode.replaceChild(newDecreaseBtn, decreaseBtn);

      newDecreaseBtn.addEventListener("click", function () {
        const quantityInput = document.querySelector(".quantity__selector");
        let quantity = parseInt(quantityInput.value);
        if (quantity > 1) {
          quantity -= 1;
          quantityInput.value = quantity;
          updateCoverageWithNewData(quantity);
        }
      });
    }

    if (increaseBtn) {
      // Remove existing listeners and add new ones
      const newIncreaseBtn = increaseBtn.cloneNode(true);
      increaseBtn.parentNode.replaceChild(newIncreaseBtn, increaseBtn);

      newIncreaseBtn.addEventListener("click", function () {
        const quantityInput = document.querySelector(".quantity__selector");
        let quantity = parseInt(quantityInput.value);
        quantity += 1;
        quantityInput.value = quantity;
        updateCoverageWithNewData(quantity);
      });
    }

    // Re-bind input event listener
    const newQuantitySelector = document.querySelector(".quantity__selector");
    if (newQuantitySelector) {
      // Remove existing listener and add new one
      const newInput = newQuantitySelector.cloneNode(true);
      newQuantitySelector.parentNode.replaceChild(
        newInput,
        newQuantitySelector
      );

      newInput.addEventListener("input", function () {
        let quantity = parseInt(this.value);
        if (quantity < 1 || isNaN(quantity)) {
          quantity = 1;
          this.value = quantity;
        }
        updateCoverageWithNewData(quantity);
      });
    }
  } catch (error) {
    // Silent error handling
  }
}

// Setup event listeners
function setupEventListeners() {
  // Listen for variant change events
  document.addEventListener("theme:variant:change", function (event) {
    try {
      const variant = event.detail?.variant;
      if (!variant?.id) {
        return;
      }

      const variantData = getVariantData(variant.id);

      // Update all fields
      updateSingleTextFields(variantData);
      updateUnitFields(variantData);
      updatePricePerMeasurement(variant, variantData);
      updateOriginalPricePerMeasurement(variant, variantData);
      updatePriceDivider(variant, variantData);
      updateSavings(variant);
      updateFloorCalculatorData(variantData);
    } catch (error) {
      // Silent error handling
    }
  });
}

// Perform initial update with current variant
function performInitialUpdate() {
  try {
    const currentVariantId = document.querySelector(
      "[data-product-select]"
    )?.value;
    if (currentVariantId) {
      const variantData = getVariantData(currentVariantId);
      if (variantData && Object.keys(variantData).length > 0) {
        // Get the full variant object for price calculation
        const productJsonElement = document.querySelector(
          'script[id*="ProductJson"]'
        );
        if (productJsonElement) {
          try {
            const productData = JSON.parse(productJsonElement.textContent);
            const currentVariant = productData.variants.find(
              (v) => v.id.toString() === currentVariantId
            );

            if (currentVariant) {
              // Update all fields including calculated price
              updateSingleTextFields(variantData);
              updateUnitFields(variantData);
              updatePricePerMeasurement(currentVariant, variantData);
              updateOriginalPricePerMeasurement(currentVariant, variantData);
              updatePriceDivider(currentVariant, variantData);
              updateSavings(currentVariant);
              updateFloorCalculatorData(variantData);
            } else {
              // Fallback to just text and unit updates
              updateSingleTextFields(variantData);
              updateUnitFields(variantData);
            }
          } catch (parseError) {
            // Fallback to just text and unit updates
            updateSingleTextFields(variantData);
            updateUnitFields(variantData);
          }
        } else {
          // Fallback to just text and unit updates
          updateSingleTextFields(variantData);
          updateUnitFields(variantData);
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }

  // Fallback: Retry initial update after a short delay in case of timing issues
  setTimeout(() => {
    try {
      const currentVariantId = document.querySelector(
        "[data-product-select]"
      )?.value;
      const priceElement = document.querySelector(
        ".product__price--per-measurement"
      );

      // Only retry if the price element exists but hasn't been updated with metaobject data
      if (currentVariantId && priceElement) {
        const hasMetaobjectData = priceElement.innerHTML.includes(
          'span class="unit-label"'
        );

        if (!hasMetaobjectData) {
          const variantData = getVariantData(currentVariantId);

          const productJsonElement = document.querySelector(
            'script[id*="ProductJson"]'
          );
          if (productJsonElement && variantData) {
            try {
              const productData = JSON.parse(productJsonElement.textContent);
              const currentVariant = productData.variants.find(
                (v) => v.id.toString() === currentVariantId
              );

              if (currentVariant) {
                updateSingleTextFields(variantData);
                updateUnitFields(variantData);
                updatePricePerMeasurement(currentVariant, variantData);
                updateSavings(currentVariant);
                updateFloorCalculatorData(variantData);
              }
            } catch (error) {
              // Silent error handling
            }
          }
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }, 500); // Wait 500ms for DOM to be fully ready
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  initializeVariantUpdater();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === "loading") {
  // Do nothing, DOMContentLoaded will fire
} else {
  // DOM is already loaded
  initializeVariantUpdater();
}
